// A single chat line. Username is color-hashed; streamer/system get tags; timestamp on hover.
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { nameColor } from "@/lib/nameColor";
import type { ChatLine } from "../ws/useStreamSocket.js";

function authorTagKey(authorType: string): "chat.anon" | "chat.system" | null {
  switch (authorType) {
    case "anon":
      return "chat.anon";
    case "system":
      return "chat.system";
    default:
      return null;
  }
}

function fmtTs(ts: number): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function ChatMessage({ line }: { line: ChatLine }) {
  const { t } = useTranslation();
  const isSystem = line.authorType === "system";
  const color = nameColor(line.id || line.name, line.isStreamer);
  const tagKey = authorTagKey(line.authorType);

  if (isSystem) {
    return (
      <div className="px-3.5 py-1 text-xs italic text-muted-foreground/70">
        <span className="break-words">{line.body}</span>
      </div>
    );
  }

  return (
    <div className="group flex flex-wrap items-baseline gap-x-2 px-3.5 py-1 text-sm leading-snug transition-colors hover:bg-white/[0.03]">
      <span className="inline-flex items-center gap-1 font-medium" style={{ color }}>
        {line.name}
        {line.isStreamer && (
          <span className="rounded-sm bg-primary/15 px-1 text-[10px] font-semibold uppercase text-primary">
            {t("chat.streamer")}
          </span>
        )}
        {tagKey && (
          <span className="rounded-sm bg-white/[0.08] px-1 text-[10px] font-medium uppercase text-muted-foreground">
            {t(tagKey)}
          </span>
        )}
      </span>
      <span className="break-words text-foreground/90">{line.body}</span>
      <span
        className={cn(
          "ml-auto shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground/60",
          "opacity-0 transition-opacity group-hover:opacity-100",
        )}
      >
        {fmtTs(line.ts)}
      </span>
    </div>
  );
}
